import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import ScaleEditModal from "@/components/forms/widgets/ScaleEditModal.tsx";
import { Search } from "@/components/search/Search.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useFormScalesDelete, useFormScalesSearch } from "@/queries/use-scales.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { AnalyticReportTypes } from "@/types/analytics/analytics.types.ts";
import { ColumnSizes } from "@/types/common.types.ts";

import { ApiFormViewScale, ApiFormViewScaleFilter } from "@/types/forms/form-view.types.ts";
import { FormScaleTypeItems } from "@/types/forms/form.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  Box,
  Button,
  ConfirmationModal,
  DropdownMenu,
  DropdownMenuItem,
  EmptyStateBase,
  FontAwesomeIcon,
  Modal,
  ModalProps,
  PaginationBlock,
  Stack,
  Table,
  Text,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useDebounce, useEffectOnce } from "react-use";

export interface Props extends ModalProps {
  onSelect?: (scale: ApiFormViewScale) => void;
}

export function ScalesSelectModal({ ...props }: Props) {
  const { onSelect, id } = props;
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();
  const library = filtersLibrary();
  const { openModal, close } = useModals();
  const { canIEdit } = usePermissions();
  const canManageScales = canIEdit(ApiPermission.form_scales);

  const {
    filter: { filter: filter },
  } = useAppFilter<ApiFormViewScaleFilter>({
    filters: [library.page_number, library.text],
    source: AnalyticReportTypes.formScale,
  });

  const { data: scales, isLoading, isFetching } = useFormScalesSearch(filter);
  const deleteScales = useFormScalesDelete();

  const [searchTerm, setSearchTerm] = useState("");
  useDebounce(
    () => {
      setSearchParams((searchParams) => {
        if (searchTerm) searchParams.set("search", searchTerm);
        else searchParams.delete("search");
        return searchParams;
      });
    },
    300,
    [searchTerm]
  );

  const handleDelete = useCallback((scale: ApiFormViewScale) => {
    openModal(ConfirmationModal, {
      question: t("surveys:delete_scale"),
      text: t("common:delete_confirm"),
      acceptTitle: t("surveys:delete_scale"),
      onAccept: async function () {
        try {
          await deleteScales.mutateAsync({ id: scale.id });

          analyticsService.trackEvent(analyticEvents.formScales.deleted, {
            [analyticProperties.id]: scale.id,
          });
        } catch {
          return;
        }
      },
    });
  }, []);

  const handleCreate = useCallback(() => {
    openModal(ScaleEditModal);
  }, []);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.formScales.viewed);
  });

  const handleEdit = useCallback((scale: ApiFormViewScale) => {
    openModal(ScaleEditModal, { formViewScale: scale });
  }, []);

  const columnHelper = createColumnHelper<ApiFormViewScale>();
  const columns = useMemo<ColumnDef<ApiFormViewScale>[]>(
    () => [
      {
        header: t("common:title"),
        cell: (row) => (
          <Stack>
            <Text className={"text-title"}>{row.row.original.title}</Text>
            <Text>{FormScaleTypeItems().find((x) => x.id == row.row.original.type)?.title}</Text>
          </Stack>
        ),
        accessorKey: "title",
        ...ColumnSizes.standard,
      },
      columnHelper.display({
        id: "actions",
        cell: (cell) => {
          return (
            <Stack className={"justify-end"} gap={"sm"} direction={"horizontal"}>
              {onSelect && (
                <Button
                  onClick={() => {
                    onSelect(cell.row.original);
                    close(id);
                  }}
                  variant={"secondary"}
                >
                  {t("common:select")}
                </Button>
              )}
              {canManageScales && (
                <>
                  {!onSelect && (
                    <Button onClick={() => handleEdit(cell.row.original)} variant={"secondary"}>
                      {t("common:edit")}
                    </Button>
                  )}

                  <DropdownMenu>
                    {onSelect && (
                      <DropdownMenuItem
                        onClick={() => handleEdit(cell.row.original)}
                        leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen-to-square"} />}
                        title={t("common:edit")}
                      />
                    )}
                    <DropdownMenuItem
                      onClick={() => handleDelete(cell.row.original)}
                      leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />}
                      variant={"menuDanger"}
                      title={t("surveys:delete_scale") + "..."}
                    />
                  </DropdownMenu>
                </>
              )}
            </Stack>
          );
        },
        ...ColumnSizes.actions,
      }),
    ],
    [scales]
  );

  return (
    <Modal
      title={t("surveys:scales")}
      variant={"side"}
      layout={"stretch"}
      headerActionsSlot={
        canManageScales ? (
          <Button
            variant={"secondary"}
            onClick={handleCreate}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
          >
            {t("surveys:create_scale")}
          </Button>
        ) : undefined
      }
      withCloser={true}
      {...props}
    >
      <>
        <Stack direction={"horizontal"}>
          <Search
            value={searchTerm}
            onInput={(e) => {
              setSearchTerm(e.currentTarget.value);
            }}
            type={"text"}
            placeholder={t("common:search_by_name")}
            className={"w-96"}
          />
        </Stack>

        <Box className={isFetching ? "opacity-50" : undefined}>
          <Table
            data={scales?.items || []}
            columns={columns}
            showSkeleton={isLoading}
            showNotFoundError={filter.text != undefined && scales?.metadata.total_items == 0}
            notFoundComponent={<EmptyStateNothingFound />}
            emptyStateComponent={
              <EmptyStateBase
                emoji={emojiDefaults.formScales.default}
                title={t("surveys:empty_state_scales_title")}
                description={t("surveys:empty_state_scales_description")}
                primaryButton={
                  canManageScales && (
                    <Button
                      onClick={() => handleCreate()}
                      size={"sm"}
                      variant={"secondary"}
                      leftIcon={<FontAwesomeIcon icon={"fa-light fa-plus"} />}
                    >
                      {t("surveys:create_scale")}
                    </Button>
                  )
                }
              />
            }
          />
        </Box>
        <PaginationBlock
          metadata={scales?.metadata}
          onPageChange={(page) => {
            setSearchParams((searchParams) => {
              searchParams.set("page", page.toString());
              return searchParams;
            });
          }}
        />
      </>
    </Modal>
  );
}
